import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import EditableText from './EditableText';
import { loadRazorpayCheckout } from '../lib/razorpay';
import { authUrlWithNext } from '../lib/whatsapp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * The Inner Circle membership popup — dark gold modal, two plan cards.
 * Yearly preselected (bigger savings). Dismissible by overlay, Escape or X.
 *
 * The actual Razorpay subscription flow:
 *   POST /api/membership/create-subscription  ->  { subscription_id, key_id }
 *   open Razorpay checkout with that subscription_id
 *   POST /api/membership/verify               ->  activates membership
 */
const InnerCircleModal = ({ open, onClose, onJoined }) => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState('yearly');
  const [busy, setBusy] = useState(false);

  // Dismiss on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleJoin = async () => {
    if (!user) {
      onClose();
      navigate(authUrlWithNext(null, '/account/membership?open=1'));
      return;
    }
    setBusy(true);
    try {
      const ok = await loadRazorpayCheckout();
      if (!ok) throw new Error('razorpay script failed to load');
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/membership/create-subscription`,
        { plan },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || data.key_id,
        subscription_id: data.subscription_id,
        name: 'Siddha Samaj',
        description: 'Inner Circle membership',
        theme: { color: '#d4a843' },
        modal: {
          ondismiss: () => setBusy(false),
        },
        handler: async (resp) => {
          try {
            await axios.post(
              `${API}/membership/verify`,
              {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_subscription_id: resp.razorpay_subscription_id,
                razorpay_signature: resp.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success('Welcome to the Inner Circle.');
            try { await refreshUser?.(); } catch (_e) {}
            onJoined?.();
            onClose();
          } catch (err) {
            toast.error(
              err?.response?.data?.detail ||
                'Payment could not be verified. Please contact support.',
            );
          } finally {
            setBusy(false);
          }
        },
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          'Payment could not be started. Please try again in a moment.',
      );
      setBusy(false);
    }
  };

  const node = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="inner-circle-modal"
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        data-testid="inner-circle-overlay"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(145deg, #1a0f05 0%, #0f0802 100%)',
          border: '1px solid rgba(212,168,67,0.35)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-primary/70 hover:text-primary"
          data-testid="inner-circle-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="text-center mb-5">
            <div className="text-[10px] font-heading tracking-[0.35em] text-primary/55 uppercase mb-2">
              <EditableText id="ic.modal.eyebrow" defaultValue="Choose your plan" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading golden-text text-glow tracking-wide">
              <EditableText id="ic.modal.heading" defaultValue="The Inner Circle" />
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5" data-testid="ic-plans">
            {[
              { key: 'monthly', label: 'Monthly', price: '₹108', sub: 'per month' },
              { key: 'yearly',  label: 'Yearly',  price: '₹1,100', sub: 'saves ₹196' },
            ].map((p) => {
              const sel = plan === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPlan(p.key)}
                  data-testid={`ic-plan-${p.key}`}
                  className={`rounded-xl px-4 py-4 text-left border transition ${
                    sel
                      ? 'bg-primary/15 border-primary'
                      : 'bg-primary/[0.03] border-primary/25 hover:border-primary/55'
                  }`}
                >
                  <div className="text-[10px] font-heading tracking-[0.3em] text-primary/60 uppercase">
                    {p.label}
                  </div>
                  <div className="mt-1 text-2xl font-heading golden-text">{p.price}</div>
                  <div className="text-[11px] text-primary/60 mt-0.5">{p.sub}</div>
                </button>
              );
            })}
          </div>

          <p className="text-[11px] text-primary/55 text-center italic mb-2 leading-relaxed">
            <EditableText
              id="ic.modal.renewalNote"
              defaultValue="Renews automatically. Cancel anytime from your account. Access continues until the end of the period you have paid for."
              multiline
            />
          </p>
          <p className="text-[11px] text-primary/70 text-center mb-4 tracking-[0.2em] uppercase">
            <EditableText id="ic.modal.cancelAnytime" defaultValue="Cancel anytime" />
          </p>

          <button
            type="button"
            onClick={handleJoin}
            disabled={busy}
            data-testid="ic-join-cta"
            className="ornate-button w-full py-3 text-sm font-heading uppercase tracking-widest text-primary rounded-lg disabled:opacity-60"
          >
            {busy ? 'Opening checkout…' : <EditableText id="ic.modal.cta" defaultValue="Join the Inner Circle" />}
          </button>

          <div className="mt-4 pt-4 border-t border-primary/15 text-center">
            <a
              href="https://wa.me/917310777189?text=Namaste%20Sanjay%20Ji%2C%20I%20have%20a%20question%20about%20the%20Inner%20Circle%20membership."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ic-queries-whatsapp"
              className="inline-flex items-center gap-2 text-[11px] font-heading uppercase tracking-[0.25em] text-primary/75 hover:text-primary"
            >
              <EditableText id="ic.modal.queries" defaultValue="For any queries · WhatsApp" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
};

export default InnerCircleModal;
